// absolute imports
import { Toolbar } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';

// icon imports
import {
  ArrowLeft as LeftIcon,
  ArrowRight as RightIcon,
} from '@mui/icons-material';

// relative imports
import { useAccountConfig } from '../../providers/AccountConfigProvider';
import { useAccount } from '../../providers/AccountProvider';
import { useModalActions } from '../../providers/ModalActionsProvider';
import LedgibleButton from '../custom-components/buttons/LedgibleButton';
import useAlternativeDisplay from '../custom-hooks/useAlternativeDisplay';

const useStyles = makeStyles()((theme) => ({
  preparerBar: {
    height: 40,
    minHeight: '0px !important',
    backgroundColor: 'green',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    color: theme.palette.common.white,
    boxSizing: 'content-box',
    transition: 'all 0.3s ease-in-out',
  },
  preparerBarText: {
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 16,
    },
  },
  preparerBarIcon: {
    marginRight: 8,
    width: 18,
  },
  preparerBarName: {
    fontWeight: theme.typography.fontWeightMedium,
    paddingLeft: 4,
  },
  backButton: {
    color: '#fff',
    borderColor: '#fff',
    backgroundColor: 'transparent',
    padding: theme.spacing(0.15, 1),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 400,
    position: 'absolute',
    right: 8,

    '&:hover': {
      color: '#fff',
      backgroundColor: 'transparent',
      borderColor: '#fff',
    },
  },
  preparerModalTrigger: {
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const ManageClientsButton = ({ btnClass, goToManageClients }) => (
  <LedgibleButton
    onClick={goToManageClients}
    onFocusVisible={(e) => e.stopPropagation()}
    color="primary"
    variant="outlined"
    buttonClasses={{
      outlinedPrimary: btnClass,
    }}
    size="small"
    textTransformOff
    startIcon={<LeftIcon fontSize="small" />}
    id="header-preparer-manage-clients-button"
  >
    Admin Dashboard
  </LedgibleButton>
);

const AdminClientViewBar = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { currentAccount: { id, name: accountName = '' } = {} } =
    useAccount() || {};
  const { configError, clearConfigError } = useAccountConfig();
  const { isMobile } = useAlternativeDisplay();
  const { showModal } = useModalActions();
  const { accountConfig } = useAccountConfig();
  const goToManageClients = useCallback(() => {
    if (!!configError) {
      clearConfigError();
    }
    navigate({ to: accountConfig?.features?.client?.adminRedirectPath || `/` });
  }, [
    clearConfigError,
    configError,
    navigate,
    accountConfig?.features?.client?.adminRedirectPath,
  ]);

  const showPreparerModal = () => {
    showModal('PREPARER_MODAL', { ManageClientsButton, goToManageClients });
  };

  return (
    <Toolbar className={classes.preparerBar}>
      {!isMobile ? (
        <>
          <div className={classes.preparerBarText}>
            Viewing client account{' '}
            <strong className={classes.preparerBarName}>
              {accountName || id}
            </strong>
          </div>
          <ManageClientsButton
            btnClass={classes.backButton}
            goToManageClients={goToManageClients}
          />
        </>
      ) : (
        <div
          className={classes.preparerModalTrigger}
          onClick={showPreparerModal}
          onKeyDown={showPreparerModal}
          role="button"
          tabIndex={-1}
        >
          Viewing Client
          <RightIcon />
        </div>
      )}
    </Toolbar>
  );
};

export default AdminClientViewBar;

ManageClientsButton.propTypes = {
  btnClass: PropTypes.string,
  goToManageClients: PropTypes.func.isRequired,
};

ManageClientsButton.defaultProps = {
  btnClass: '',
};
